import { create } from 'zustand';
import { Order, MainWorkOrder, SubWorkOrder, RushOrder, ProductionReport as ProductionReportType } from '@/types';

interface OrderState {
  orders: Order[];
  mainWorkOrders: MainWorkOrder[];
  subWorkOrders: SubWorkOrder[];
  rushOrders: RushOrder[];
  productionReports: ProductionReportType[];

  // Actions
  setOrders: (orders: Order[]) => void;
  setMainWorkOrders: (orders: MainWorkOrder[]) => void;
  setSubWorkOrders: (orders: SubWorkOrder[]) => void;
  setRushOrders: (orders: RushOrder[]) => void;

  addOrder: (order: Order) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  deleteOrder: (id: string) => void;

  addMainWorkOrder: (order: MainWorkOrder) => void;
  updateMainWorkOrder: (id: string, updates: Partial<MainWorkOrder>) => void;
  deleteMainWorkOrder: (id: string) => void;

  addSubWorkOrder: (order: SubWorkOrder) => void;
  updateSubWorkOrder: (id: string, updates: Partial<SubWorkOrder>) => void;
  deleteSubWorkOrder: (id: string) => void;

  addRushOrder: (order: RushOrder) => void;
  updateRushOrder: (id: string, updates: Partial<RushOrder>) => void;

  reorderOrders: (orderedIds: string[]) => void;
  cascadeDelayFromSubOrder: (subOrderId: string, delayDays: number) => void;
  checkAndUpdateOvertimeStatus: () => void;

  addProductionReport: (report: ProductionReportType) => void;
  getTodayReportsCount: () => number;
  getProductionReportsBySubOrderId: (subOrderId: string) => ProductionReportType[];

  // Getters
  getOrderById: (id: string) => Order | undefined;
  getMainWorkOrdersByOrderId: (orderId: string) => MainWorkOrder[];
  getSubWorkOrdersByMainOrderId: (mainOrderId: string) => SubWorkOrder[];
  getRushOrderById: (id: string) => RushOrder | undefined;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  // Initial state
  orders: [],
  mainWorkOrders: [],
  subWorkOrders: [],
  rushOrders: [],
  productionReports: [],

  // Setters
  setOrders: (orders) => set({ orders }),
  setMainWorkOrders: (orders) => set({ mainWorkOrders: orders }),
  setSubWorkOrders: (orders) => set({ subWorkOrders: orders }),
  setRushOrders: (orders) => set({ rushOrders: orders }),
  setProductionReports: (reports) => set({ productionReports: reports }),

  // Order actions
  addOrder: (order) => set((state) => ({ orders: [...state.orders, order] })),

  updateOrder: (id, updates) =>
    set((state) => ({
      orders: state.orders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  deleteOrder: (id) =>
    set((state) => ({
      orders: state.orders.filter((order) => order.id !== id),
    })),

  // MainWorkOrder actions
  addMainWorkOrder: (order) =>
    set((state) => ({ mainWorkOrders: [...state.mainWorkOrders, order] })),

  updateMainWorkOrder: (id, updates) =>
    set((state) => ({
      mainWorkOrders: state.mainWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  deleteMainWorkOrder: (id) =>
    set((state) => {
      const mwo = state.mainWorkOrders.find((o) => o.id === id);
      // 同時移除所屬的子工單，並更新父訂單的 mainWorkOrders 列表
      return {
        mainWorkOrders: state.mainWorkOrders.filter((o) => o.id !== id),
        subWorkOrders: state.subWorkOrders.filter(
          (s) => s.mainOrderId !== id
        ),
        orders: mwo
          ? state.orders.map((o) =>
              o.id === mwo.orderId
                ? { ...o, mainWorkOrders: o.mainWorkOrders.filter((mId) => mId !== id) }
                : o
            )
          : state.orders,
      };
    }),

  // SubWorkOrder actions
  addSubWorkOrder: (order) =>
    set((state) => ({ subWorkOrders: [...state.subWorkOrders, order] })),

  updateSubWorkOrder: (id, updates) =>
    set((state) => ({
      subWorkOrders: state.subWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  deleteSubWorkOrder: (id) =>
    set((state) => {
      const swo = state.subWorkOrders.find((o) => o.id === id);
      // 同時更新父制令單的 subWorkOrders 列表
      return {
        subWorkOrders: state.subWorkOrders.filter((o) => o.id !== id),
        mainWorkOrders: swo
          ? state.mainWorkOrders.map((m) =>
              m.id === swo.mainOrderId
                ? { ...m, subWorkOrders: m.subWorkOrders.filter((sId) => sId !== id) }
                : m
            )
          : state.mainWorkOrders,
      };
    }),

  // RushOrder actions
  addRushOrder: (order) =>
    set((state) => ({ rushOrders: [...state.rushOrders, order] })),

  updateRushOrder: (id, updates) =>
    set((state) => ({
      rushOrders: state.rushOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // 拖曳排序：依照新的 ID 順序更新 priority
  reorderOrders: (orderedIds) =>
    set((state) => ({
      orders: state.orders.map((order) => {
        const newPriority = orderedIds.indexOf(order.id);
        return newPriority >= 0 ? { ...order, priority: newPriority + 1 } : order;
      }),
    })),

  // 級聯延遲：推延目標派工單及其後續所有同制令單的派工單
  cascadeDelayFromSubOrder: (subOrderId, delayDays) =>
    set((state) => {
      const targetSub = state.subWorkOrders.find((s) => s.id === subOrderId);
      if (!targetSub) return state;

      // 找出同制令單的所有派工單，按 plannedStart 排序
      const siblings = state.subWorkOrders
        .filter((s) => s.mainOrderId === targetSub.mainOrderId)
        .sort((a, b) => new Date(a.plannedStart).getTime() - new Date(b.plannedStart).getTime());

      // 找出目標派工單在排序中的索引，它和之後的都要延遲
      const targetIdx = siblings.findIndex((s) => s.id === subOrderId);
      const toDelayIds = new Set(siblings.slice(targetIdx).map((s) => s.id));

      const delayMs = delayDays * 24 * 60 * 60 * 1000;

      const updatedSubOrders = state.subWorkOrders.map((s) => {
        if (!toDelayIds.has(s.id)) return s;
        return {
          ...s,
          plannedStart: new Date(new Date(s.plannedStart).getTime() + delayMs).toISOString(),
          plannedEnd: new Date(new Date(s.plannedEnd).getTime() + delayMs).toISOString(),
        };
      });

      // 更新制令單的 plannedEnd 為其最晚的派工單 plannedEnd
      const mainOrderId = targetSub.mainOrderId;
      const mainSubs = updatedSubOrders.filter((s) => s.mainOrderId === mainOrderId);
      const latestEnd = mainSubs.reduce(
        (max, s) => Math.max(max, new Date(s.plannedEnd).getTime()),
        0
      );

      const updatedMainOrders = state.mainWorkOrders.map((m) => {
        if (m.id !== mainOrderId) return m;
        return { ...m, plannedEnd: new Date(latestEnd).toISOString() };
      });

      return { subWorkOrders: updatedSubOrders, mainWorkOrders: updatedMainOrders };
    }),

  // 掃描所有派工單，更新逾期狀態
  checkAndUpdateOvertimeStatus: () =>
    set((state) => {
      const now = Date.now();
      return {
        subWorkOrders: state.subWorkOrders.map((s) => {
          if (s.status === '已完成') return s;
          const isOvertime = new Date(s.plannedEnd).getTime() < now;
          return isOvertime !== s.isOvertime ? { ...s, isOvertime } : s;
        }),
      };
    }),

  // Getters
  getOrderById: (id) => get().orders.find((order) => order.id === id),

  getMainWorkOrdersByOrderId: (orderId) =>
    get().mainWorkOrders.filter((order) => order.orderId === orderId),

  getSubWorkOrdersByMainOrderId: (mainOrderId) =>
    get().subWorkOrders.filter((order) => order.mainOrderId === mainOrderId),

  getRushOrderById: (id) => get().rushOrders.find((order) => order.id === id),

  // Production report actions
  addProductionReport: (report) =>
    set((state) => {
      // 同時更新生產報工記錄和子工單的實際產出數量
      const updatedSubOrders = state.subWorkOrders.map((order) =>
        order.id === report.subWorkOrderId
          ? { ...order, actualQuantity: order.actualQuantity + report.goodQuantity }
          : order
      );

      return {
        productionReports: [...state.productionReports, report],
        subWorkOrders: updatedSubOrders,
      };
    }),

  getTodayReportsCount: () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return get().productionReports.filter((report) =>
      report.reportDate.startsWith(today)
    ).length;
  },

  getProductionReportsBySubOrderId: (subOrderId) =>
    get().productionReports.filter((report) => report.subWorkOrderId === subOrderId),
}));
